import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { ImagePlus, RotateCcw, User, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase 92D.2 — report-owned presentation fields: Lead Reviewer, report photo
 * override (Use Profile / Upload / Reset), and the Next-Review-Period closing
 * (Primary Focus, Key Message, Next Review Date). Self-contained; persists via
 * the authoring endpoint + report photo upload. Never touches Hub profile photo.
 */
export const ReportPresentationEditor = ({ reportId, report, onSaved }) => {
  const [leadReviewer, setLeadReviewer] = useState(report.lead_reviewer || '');
  const [primaryFocus, setPrimaryFocus] = useState(report.primary_focus || '');
  const [keyMessage, setKeyMessage] = useState(report.key_message || '');
  const [nextReview, setNextReview] = useState(report.next_review_date || '');
  const [useProfile, setUseProfile] = useState(report.use_profile_photo !== false && !report.report_photo_url);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const patch = async (body) => {
    try { await axios.put(`${API}/development-reports/${reportId}/authoring`, body); onSaved && onSaved(); }
    catch (e) { toast.error('Save failed'); }
  };

  const useProfilePhoto = async () => { setUseProfile(true); await patch({ use_profile_photo: true, report_photo_url: null, report_photo_object_key: null }); toast.success('Using athlete profile photo'); };
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/development-reports/${reportId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUseProfile(false); onSaved && onSaved(); toast.success('Report photo uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4" data-testid="report-presentation-editor">
      <div>
        <Label className="text-xs text-slate-500">Athlete Photo</Label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-slate-500">{useProfile ? 'Using athlete profile photo' : 'Using report-specific photo'}</span>
          <Button variant="outline" size="sm" onClick={useProfilePhoto} data-testid="photo-use-profile"><User className="mr-1 h-3.5 w-3.5" /> Use Profile Photo</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload">
            {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1 h-3.5 w-3.5" />} Upload Report Photo
          </Button>
          <Button variant="ghost" size="sm" onClick={useProfilePhoto} data-testid="photo-reset"><RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset</Button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-slate-500">Lead Reviewer</Label>
          <Input value={leadReviewer} onChange={(e) => setLeadReviewer(e.target.value)} onBlur={() => patch({ lead_reviewer: leadReviewer })} placeholder="e.g. J. Silva (Technical Coach)" data-testid="lead-reviewer-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Next Review Date</Label>
          <Input type="date" value={nextReview} onChange={(e) => setNextReview(e.target.value)} onBlur={() => patch({ next_review_date: nextReview })} data-testid="next-review-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Primary Development Focus (closing)</Label>
        <Textarea value={primaryFocus} onChange={(e) => setPrimaryFocus(e.target.value)} onBlur={() => patch({ primary_focus: primaryFocus })} rows={2} placeholder="Short forward-looking summary for the next period." data-testid="primary-focus-input" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Key Message for the Player (callout)</Label>
        <Textarea value={keyMessage} onChange={(e) => setKeyMessage(e.target.value)} onBlur={() => patch({ key_message: keyMessage })} rows={2} placeholder="A direct, motivating message shown as a highlighted callout." data-testid="key-message-input" />
      </div>
    </div>
  );
};

export default ReportPresentationEditor;

/** Per-section concise summary shown in the FINAL document instead of the full
 * staff assessment. Full staff text remains preserved in the builder/DB. */
export const ConciseSummaryEditor = ({ reportId, sectionKey, initial, onSaved }) => {
  const [val, setVal] = useState(initial || '');
  const save = async () => {
    try {
      await axios.put(`${API}/development-reports/${reportId}/authoring`, { section_summaries: { [sectionKey]: val } });
      onSaved && onSaved();
    } catch (e) { toast.error('Save failed'); }
  };
  return (
    <div className="mt-3" data-testid={`concise-summary-${sectionKey}`}>
      <Label className="text-xs text-slate-500">Concise summary for final document <span className="text-slate-400">(optional — shown instead of the full assessment)</span></Label>
      <Textarea value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} rows={2} placeholder="A 1–3 sentence version for the parent/player document." data-testid={`concise-summary-input-${sectionKey}`} />
    </div>
  );
};
